import { useState, useMemo } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { RiEdit2Line, RiDeleteBin6Line, RiEyeLine } from 'react-icons/ri'
import { HiChevronDown, HiChevronUp, 
  HiBuildingOffice2 
} from 'react-icons/hi2'
import { UserAvatar } from '../../../components/UserAvatar'
import { Button } from '../../../components'
import type { User } from '../../../models'

interface UsersTableProps {
  users: User[]
  loading: boolean
  onView: (user: User) => void
  onEdit: (user: User) => void
  onDelete: (user: User) => void
  onManageSectors: (user: User) => void
  selectedUserIds: number[]
  onSelectUser: (userId: number) => void
  onSelectAll: () => void
  isAdmin?: boolean // Adicionar prop para controlar permissões
}

type SortField = 'name' | 'email' | 'cargo_usuario' | 'funcao_usuario'
type SortDirection = 'asc' | 'desc'

export const UsersTable = ({
  users,
  loading,
  onView,
  onEdit,
  onDelete,
  onManageSectors,
  selectedUserIds,
  onSelectUser,
  onSelectAll,
  isAdmin = true, // Default true para manter comportamento anterior
}: UsersTableProps) => {
  const [sortField, setSortField] = useState<SortField>('name')
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc')

  // Sort users
  const sortedUsers = useMemo(() => {
    const sorted = [...users].sort((a, b) => {
      let aValue = a[sortField] || ''
      let bValue = b[sortField] || ''

      if (typeof aValue === 'string') aValue = aValue.toLowerCase()
      if (typeof bValue === 'string') bValue = bValue.toLowerCase()

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1
      return 0
    })
    return sorted
  }, [users, sortField, sortDirection])

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDirection('asc')
    }
  }

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return null
    return sortDirection === 'asc' ? (
      <HiChevronUp className='w-3.5 h-3.5 ml-1' />
    ) : (
      <HiChevronDown className='w-3.5 h-3.5 ml-1' />
    )
  }

  const getFuncaoBadgeColor = (funcao: string) => {
    switch (funcao) {
      case 'ADMIN':
        return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
      case 'DIRETOR':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400'
      case 'USUARIO':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400'
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400'
    }
  }

  const allSelected = users.length > 0 && selectedUserIds.length === users.length
  const someSelected = selectedUserIds.length > 0 && selectedUserIds.length < users.length

  if (loading) {
    return (
      <div className='flex items-center justify-center py-12'>
        <div className='text-center'>
          <div className='inline-flex p-4 rounded-full bg-blue-100 dark:bg-blue-900/30 mb-4'>
            <svg
              className='animate-spin w-8 h-8 text-blue-600 dark:text-blue-400'
              fill='none'
              viewBox='0 0 24 24'
            >
              <circle
                className='opacity-25'
                cx='12'
                cy='12'
                r='10'
                stroke='currentColor'
                strokeWidth='4'
              ></circle>
              <path
                className='opacity-75'
                fill='currentColor'
                d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
              ></path>
            </svg>
          </div>
          <p className='text-gray-600 dark:text-gray-400 text-sm'>Carregando usuários...</p>
        </div>
      </div>
    )
  }

  if (users.length === 0) {
    return (
      <div className='flex flex-col items-center justify-center py-12'>
        <div className='inline-flex p-4 rounded-full bg-gray-100 dark:bg-gray-800 mb-4'>
          <RiEyeLine className='w-8 h-8 text-gray-400' />
        </div>
        <h3 className='text-base font-semibold text-gray-900 dark:text-gray-100 mb-2'>
          Nenhum usuário encontrado
        </h3>
        <p className='text-sm text-gray-600 dark:text-gray-400 text-center max-w-md'>
          Não há usuários que correspondam aos filtros aplicados.
        </p>
      </div>
    )
  }

  return (
    <div className='overflow-x-auto max-h-[calc(100vh-350px)] overflow-y-auto'>
      <table className='w-full text-sm  border-collapse'>
        <thead className='bg-gray-50 dark:bg-neutral-700/50 border-b border-gray-200 dark:border-neutral-600'>
          <tr>
            <th className='px-3 py-2.5 text-left w-10'>
              <input
                type='checkbox'
                checked={allSelected}
                ref={input => {
                  if (input) {
                    input.indeterminate = someSelected
                  }
                }}
                onChange={onSelectAll}
                className='w-4 h-4 text-blue-600 bg-white dark:bg-neutral-700 border-gray-300 dark:border-neutral-600 rounded focus:ring-blue-500 focus:ring-2'
              />
            </th>
            <th
              className='px-3 py-2.5 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-neutral-700 transition-colors'
              onClick={() => handleSort('name')}
            >
              <div className='flex items-center'>
                Usuário
                <SortIcon field='name' />
              </div>
            </th>
            <th
              className='px-3 py-2.5 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-neutral-700 transition-colors'
              onClick={() => handleSort('email')}
            >
              <div className='flex items-center'>
                Email
                <SortIcon field='email' />
              </div>
            </th>
            <th
              className='px-3 py-2.5 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-neutral-700 transition-colors'
              onClick={() => handleSort('cargo_usuario')}
            >
              <div className='flex items-center'>
                Cargo
                <SortIcon field='cargo_usuario' />
              </div>
            </th>
            <th
              className='px-3 py-2.5 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-neutral-700 transition-colors'
              onClick={() => handleSort('funcao_usuario')}
            >
              <div className='flex items-center'>
                Função
                <SortIcon field='funcao_usuario' />
              </div>
            </th>
            <th className='px-3 py-2.5 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider'>
              Setores
            </th>
            <th className='px-3 py-2.5 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-32'>
              Ações
            </th>
          </tr>
        </thead>
        <tbody className='bg-white dark:bg-neutral-800 divide-y divide-gray-200 dark:divide-neutral-700'>
          <AnimatePresence>
            {sortedUsers.map((user, index) => {
              const isSelected = selectedUserIds.includes(user.id)

              return (
                <motion.tr
                  key={user.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ delay: index * 0.02, duration: 0.15 }}
                  className={`hover:bg-gray-50 dark:hover:bg-neutral-700/50 transition-colors ${
                    isSelected ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                  }`}
                >
                  <td className='px-3 py-2.5'>
                    <input
                      type='checkbox'
                      checked={isSelected}
                      onChange={() => onSelectUser(user.id)}
                      className='w-4 h-4 text-blue-600 bg-white dark:bg-neutral-700 border-gray-300 dark:border-neutral-600 rounded focus:ring-blue-500 focus:ring-2'
                    />
                  </td>
                  <td className='px-3 py-2.5 whitespace-nowrap'>
                    <div className='flex items-center gap-2'>
                      <UserAvatar user={user} size='sm' showName={false} showPopup={true} />
                      <span className='text-sm font-medium text-gray-900 dark:text-gray-100'>
                        {user.name}
                      </span>
                    </div>
                  </td>
                  <td className='px-3 py-2.5'>
                    <div className='text-sm text-gray-700 dark:text-gray-300'>{user.email}</div>
                  </td>
                  <td className='px-3 py-2.5'>
                    <div className='text-sm text-gray-700 dark:text-gray-300'>
                      {user.cargo_usuario || '-'}
                    </div>
                  </td>
                  <td className='px-3 py-2.5 whitespace-nowrap'>
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getFuncaoBadgeColor(user.funcao_usuario)}`}
                    >
                      {user.funcao_usuario}
                    </span>
                  </td>
                  <td className='px-3 py-2.5'>
                    <div className='flex flex-wrap gap-1 max-w-xs'>
                      {user.setores && user.setores.length > 0 ? (
                        <>
                          {user.setores.slice(0, 2).map(setor => (
                            <span
                              key={setor.id}
                              className='inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-gray-100 text-gray-700 dark:bg-neutral-700 dark:text-gray-300'
                            >
                              {setor.nome_setor}
                            </span>
                          ))}
                          {user.setores.length > 2 && (
                            <span className='inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-gray-200 text-gray-600 dark:bg-neutral-600 dark:text-gray-400'>
                              +{user.setores.length - 2}
                            </span>
                          )}
                        </>
                      ) : (
                        <span className='text-xs text-gray-400 dark:text-gray-600'>-</span>
                      )}
                    </div>
                  </td>
                  <td className='px-3 py-2.5 whitespace-nowrap text-right'>
                    <div className='flex items-center justify-end gap-1'>
                      <Button
                        variant='ghost'
                        size='icon'
                        onClick={() => onView(user)}
                        className='h-8 w-8 text-blue-600 hover:text-blue-700 hover:bg-blue-50 dark:text-blue-400 dark:hover:text-blue-300 dark:hover:bg-blue-900/30'
                        title='Visualizar'
                      >
                        <RiEyeLine className='w-4 h-4' />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => onManageSectors(user)}
                        className="h-8 w-8 text-purple-600 hover:text-purple-700 hover:bg-purple-50 dark:text-purple-400 dark:hover:text-purple-300 dark:hover:bg-purple-900/30"
                        title="Gerenciar Setores"
                        disabled={!isAdmin}
                      >
                        <HiBuildingOffice2 className="w-4 h-4" />
                      </Button>
                      <Button
                        variant='ghost'
                        size='icon'
                        onClick={() => onEdit(user)}
                        className='h-8 w-8 text-yellow-600 hover:text-yellow-700 hover:bg-yellow-50 dark:text-yellow-400 dark:hover:text-yellow-300 dark:hover:bg-yellow-900/30'
                        title='Editar'
                        disabled={!isAdmin}
                      >
                        <RiEdit2Line className='w-4 h-4' />
                      </Button>
                      <Button
                        variant='ghost'
                        size='icon'
                        onClick={() => onDelete(user)}
                        className='h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:text-red-300 dark:hover:bg-red-900/30'
                        title='Desativar'
                        disabled={!isAdmin}
                      >
                        <RiDeleteBin6Line className='w-4 h-4' />
                      </Button>
                    </div>
                  </td>
                </motion.tr>
              )
            })}
          </AnimatePresence>
        </tbody>
      </table>
    </div>
  )
}
